import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X, Upload, Camera, Image as ImageIcon, FileText } from 'lucide-react';
import { toast } from '../notifications';
import FormField from '../ui/FormField.jsx';
import { Input, Textarea } from '../ui/Input.jsx';
import Modal from '../ui/Modal.jsx';
import Button from '../ui/Button.jsx';
import StarRating from '../ui/StarRating.jsx';
import { toLocalDateString } from '../../utils/timeFormatUtils';

// Formular fuer manuelle Eintraege (anlegen + bearbeiten), als Fullscreen-Modal.
//
// Aufbau folgt seit September 2026 dem Session-Feedback (components/SessionFeedback.js):
// Thema, Wann/Dauer, Sterne, dann dieselben drei Fragen in derselben Reihenfolge
// ("Was hast du gelernt?", "Reflexion", "Naechste Schritte"). Vorher standen hier
// zusaetzlich "Was hast du gemacht?" (doppelt zu "Was hast du gelernt?") und ein
// Tag-Feld, das es im Feedback nicht gibt. Tags werden weder angezeigt noch
// geschickt — die Backend-Route behaelt beim Bearbeiten vorhandene Tags bei
// (learning-journal.js, PUT /entries/:id), es geht also nichts verloren.
// Upload-Logik bewusst unveraendert (1:1 aus LearningJournalPage V1).
const ManualEntryForm = ({ entry, onSave, onClose }) => {
  const [title, setTitle] = useState(entry?.title || '');
  const [learningResults, setLearningResults] = useState(entry?.learningResults || '');
  const [reflection, setReflection] = useState(entry?.reflection || '');
  const [nextSteps, setNextSteps] = useState(entry?.nextSteps || '');
  const [rating, setRating] = useState(entry?.rating || 0);
  const [durationMinutes, setDurationMinutes] = useState(entry?.durationMinutes ?? '');
  // toLocalDateString statt toISOString(): toISOString() rechnet nach UTC um. In
  // Hamburg (UTC+2) waere ein Eintrag um 00:30 Uhr sonst auf den Vortag datiert.
  const [occurredAtDate, setOccurredAtDate] = useState(() => {
    const parsed = entry?.occurredAt ? new Date(entry.occurredAt) : new Date();
    return toLocalDateString(Number.isNaN(parsed.getTime()) ? new Date() : parsed);
  });
  const [occurredAtTime, setOccurredAtTime] = useState(() => {
    if (!entry?.occurredAt) return '';
    const parsed = new Date(entry.occurredAt);
    if (Number.isNaN(parsed.getTime())) return '';
    const h = String(parsed.getHours()).padStart(2, '0');
    const m = String(parsed.getMinutes()).padStart(2, '0');
    return (h === '00' && m === '00') ? '' : `${h}:${m}`;
  });
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState(entry?.files || []);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  const validateAndAddFiles = (fileList) => {
    const validFiles = [];
    for (const file of fileList) {
      const maxSize = file.type.startsWith('image/') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`„${file.name}“ ist zu groß. Max. ${maxSize / 1024 / 1024}MB.`);
        continue;
      }
      const isValidType = /^(image\/.*|application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|text\/plain)$/.test(file.type);
      if (!isValidType) {
        toast.error(`„${file.name}“ hat ein nicht unterstütztes Format.`);
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length + files.length + existingFiles.length > 5) {
      toast.error('Maximal 5 Dateien erlaubt');
      return;
    }
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileChange = (e) => {
    validateAndAddFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeNewFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (fileId) => {
    setExistingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFilePreviewUrl = (file) => file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Bitte gib deinem Eintrag ein Thema');
      return;
    }
    setIsSaving(true);
    try {
      // Datum und Uhrzeit sind die Uhrzeit auf dem Geraet der Person. Als ISO-String
      // MIT Zeitzone schicken: Ohne Angabe las der Server (laeuft auf UTC) "20:00" als
      // 20:00 UTC, angezeigt wurde 22:00 - und jedes erneute Speichern verschob den
      // Eintrag um weitere 1-2 Stunden (A43). Nur-Datum wird zu 00:00 Uhr lokal, das
      // Formular zeigt beim Bearbeiten dann wieder keine Uhrzeit an.
      let occurredAt = null;
      if (occurredAtDate) {
        const local = new Date(`${occurredAtDate}T${occurredAtTime || '00:00'}`);
        occurredAt = Number.isNaN(local.getTime()) ? null : local.toISOString();
      }

      const savedEntry = await onSave({
        title: title.trim(),
        learningResults: learningResults.trim() || null,
        reflection: reflection.trim() || null,
        nextSteps: nextSteps.trim() || null,
        rating: rating > 0 ? rating : null,
        durationMinutes: durationMinutes !== '' ? parseInt(durationMinutes, 10) : null,
        occurredAt,
        _files: files,
        _removedFileIds: (entry?.files || [])
          .filter(f => !existingFiles.some(ef => ef.id === f.id))
          .map(f => f.id),
      });
      if (!savedEntry) setIsSaving(false);
    } catch (error) {
      console.error('Fehler beim Speichern des Eintrags:', error);
      toast.error('Eintrag konnte nicht gespeichert werden');
      setIsSaving(false);
    }
  };

  // Dieselbe Beschriftung, die ui/FormField seinen Labels gibt — fuer die zwei
  // Stellen, an denen kein einzelnes Feld beschriftet wird (Sterne, Dateien).
  const labelClass = "block text-sm font-medium text-foreground-2";
  const fileCount = files.length + existingFiles.length;

  return (
    /* Kanonisches Modal statt eigenem `fixed inset-0 z-[60]`: Das native
       <dialog> landet im Top-Layer des Browsers — dadurch lag die schwebende
       Navigationsleiste (z-50) vorher ueber den Formular-Knoepfen. Auf dem
       Telefon Vollbild (fullscreenOnMobile) — als Bottom-Sheet blieb von dem
       langen Formular nur die Ueberschrift sichtbar, sobald die Tastatur aufging.
       Am Desktop weiterhin eine zentrierte Karte. */
    <Modal
      fullscreenOnMobile
      open
      onClose={onClose}
      size="lg"
      title={entry ? 'Eintrag bearbeiten' : 'Neuer Logbucheintrag'}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-none">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !title.trim()}
            className="flex-1 sm:flex-none"
          >
            {isSaving ? 'Speichert…' : 'Speichern'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {/* Thema — wie bei einer Session das einzige Pflichtfeld. Was gemacht wurde,
            steht in einem Wort hier; was dabei herauskam, unten bei "gelernt". */}
        <FormField label="Thema" required>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Englisch-Vokabeln"
            maxLength={200}
          />
        </FormField>

        {/* Datum, Uhrzeit und Dauer in EINER Zeile.

            Breite gehoert an die Huelle, NICHT ans Feld (September 2026): ui/Input
            bringt `w-full` selbst mit und haengt die uebergebene className nur an.
            Es gibt kein tailwind-merge im Projekt, also standen `w-full` und
            `w-[120px]` gleichberechtigt im Klassenattribut und die Reihenfolge im
            gebauten Stylesheet entschied — `w-full` gewann. Damit hatte das
            Uhrzeitfeld die Flex-Basis 100 % und drueckte das Datumsfeld (flex-1,
            Basis 0) auf Null: im Screenshot vom 7.9.2026 blieb vom Datum nur "07"
            stehen, daneben ein breiter leerer Kasten.

            Jetzt sitzen flex/min-width auf den FormField-Huellen, die Felder fuellen
            mit ihrem eigenen `w-full` genau ihre Zelle. `flex-wrap` ist der
            Sicherheitsgurt: Passt die Dauer auf einem schmalen Telefon (360px)
            nicht mehr daneben, rutscht sie in die naechste Zeile, statt gequetscht
            zu werden. Die Mindestbreiten (9 + 6.5 + 5.5 rem + 2 Luecken = 348px)
            sind auf 390px-iPhones bemessen (350px Inhalt); Chrome/Android
            zeichnet zusaetzlich Kalender- und Uhr-Symbol in die Felder, deshalb
            bekommen Datum und Uhrzeit die Reserve, nicht die Dauer.
            Jedes Feld traegt eine eigene Beschriftung — ein leeres <input type="time">
            zeigt auf iOS mit `appearance: none` sonst einen Kasten ohne Zweck. */}
        <div className="flex flex-wrap gap-1.5">
          <FormField label="Datum" className="min-w-[9rem] flex-1">
            <Input
              id="entry-occurred-date"
              type="date"
              value={occurredAtDate}
              max={toLocalDateString(new Date())}
              onChange={(e) => setOccurredAtDate(e.target.value)}
            />
          </FormField>
          <FormField label="Uhrzeit" className="min-w-[6.5rem] max-w-[7rem] flex-1">
            <Input
              id="entry-occurred-time"
              type="time"
              value={occurredAtTime}
              onChange={(e) => setOccurredAtTime(e.target.value)}
            />
          </FormField>
          <FormField label="Dauer (Min.)" className="min-w-[5.5rem] max-w-[7rem] flex-1">
            <Input
              id="entry-duration"
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              placeholder="45"
              min={0}
              max={360}
            />
          </FormField>
        </div>

        {/* Sternebewertung — Frage links, Sterne rechts, eine Zeile (wie im
            Session-Feedback). Nochmaliges Tippen hebt die Wahl auf: die Bewertung
            ist hier freiwillig. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <span id="entry-rating-label" className={labelClass}>Wie lief es?</span>
          <StarRating
            value={rating}
            onChange={setRating}
            labelledBy="entry-rating-label"
            allowClear
            align="end"
          />
        </div>

        {/* Dieselben drei Fragen wie im Session-Feedback, gleiche Reihenfolge,
            gleiche Formulierungen. */}
        <FormField label="Was hast du gelernt?">
          <Textarea
            value={learningResults}
            onChange={(e) => setLearningResults(e.target.value)}
            placeholder="Was hast du gelernt oder geübt?"
            rows={3}
          />
        </FormField>

        <FormField label="Reflexion">
          <Textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder="Wie lief es für dich? Was ist gut gelaufen, was könnte besser werden?"
            rows={2}
          />
        </FormField>

        <FormField label="Nächste Schritte">
          <Textarea
            value={nextSteps}
            onChange={(e) => setNextSteps(e.target.value)}
            placeholder="Was möchtest du als Nächstes lernen oder vertiefen?"
            rows={2}
          />
        </FormField>

        {/* Dateien & Fotos */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <h4 className={labelClass}>Dokumente & Fotos</h4>
            <span className="text-xs text-muted-foreground">{fileCount}/5 Dateien</span>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={fileCount >= 5}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground font-medium hover:border-primary/50 hover:bg-primary-soft/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              Datei wählen
            </button>
            <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" multiple accept="image/*,.pdf,.doc,.docx,.txt" />

            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={fileCount >= 5}
              className="flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground font-medium hover:border-primary/50 hover:bg-primary-soft/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Camera className="h-4 w-4" aria-hidden="true" />
              Foto
            </button>
            <input ref={cameraInputRef} type="file" onChange={handleFileChange} className="hidden" accept="image/*" capture="environment" />
          </div>

          {/* Bestehende Dateien (beim Bearbeiten) */}
          {existingFiles.length > 0 && (
            <div className="space-y-2">
              {existingFiles.map((file) => (
                <div key={file.id} className="p-2.5 bg-surface-raised rounded-xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                    {file.type?.startsWith('image/') ? <ImageIcon className="h-5 w-5 text-muted-foreground" /> : <FileText className="h-5 w-5 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground-2 truncate font-medium">{file.name}</p>
                    {file.size && <p className="text-xs text-faint">{formatFileSize(file.size)}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExistingFile(file.id)}
                    aria-label={`Datei ${file.name} entfernen`}
                    className="p-1.5 hover:bg-danger-soft rounded-lg transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <X className="h-4 w-4 text-faint hover:text-danger" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Neue Dateien (noch nicht hochgeladen) */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, index) => {
                const isImage = file.type.startsWith('image/');
                return (
                  <div key={index} className="p-2.5 bg-surface-raised rounded-xl flex items-center gap-3">
                    {isImage ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-surface-raised">
                        <img src={getFilePreviewUrl(file)} alt={file.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground-2 truncate font-medium">{file.name}</p>
                      <p className="text-xs text-faint">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNewFile(index)}
                      aria-label={`Datei ${file.name} entfernen`}
                      className="p-1.5 hover:bg-danger-soft rounded-lg transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <X className="h-4 w-4 text-faint hover:text-danger" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

ManualEntryForm.propTypes = {
  entry: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ManualEntryForm;
